import styles from './ui.module.css';
import { Logo } from './brand';
import { ChromeLink } from './chrome-link';
import {
  COMPANY,
  configuredSocials,
  LEGAL_NAV,
  SITE_CHROME,
  supportAddress,
  type FooterColumn,
} from '@goodbuild/rules/site';
import type { SiteId } from '@goodbuild/rules/sites';

/**
 * The site footer, which is different on each of the three sites.
 *
 * The store's is grouped by who is looking — a player finds the library, a
 * developer finds listing — because the store is where all three audiences
 * arrive first. The creators' and developers' footers are grouped by the work
 * instead, since anyone on those domains has already self-selected.
 *
 * Entries that leave the site render as anchors rather than `next/link`; see
 * `ChromeLink`.
 *
 * **The legal documents are a column now, and they used to be a row along the
 * bottom edge.** They sat in the colophon beside the social links, at label
 * size in the muted ramp — the same treatment as the copyright line, which is
 * how a reader reads them: as furniture. They are not. `Refunds` is a page
 * somebody comes to the footer specifically to find, and `Terms` is what a
 * buyer agreed to. Named, and set like every other footer link, they are
 * something a person can scan for.
 *
 * The column is appended here rather than written into each site's
 * `SITE_CHROME` entry, and that is deliberate: `LEGAL_NAV` is one list, and
 * copying it into three site maps would be three arrays that have to stay
 * identical on the one list where drift is a compliance problem rather than a
 * broken link. Every site gets the column without anybody remembering to add
 * it.
 *
 * Social links render only when configured. Section 29.2: a footer with six
 * plausible-looking icons that all go nowhere looks finished and helps nobody,
 * so an unconfigured account is absent rather than dead.
 */
export function SiteFooter({ site }: { site: SiteId }): React.ReactElement {
  /*
   * **`appOrigin` and `storeOrigin` were props here and are gone.**
   *
   * This footer lists `Your library` and `Orders` beside `Browse games` and
   * `About`, and the first two used to be the React app's on another host while
   * the last two were the storefront's. One deployment answers for all four, so
   * every entry stays the relative path `SITE_CHROME` holds.
   */
  const chrome = SITE_CHROME[site];
  const socials = configuredSocials();
  const support = supportAddress();
  const year = new Date().getFullYear();

  /*
   * The site's own columns, and then the obligation.
   *
   * Last on purpose. The grid is `auto-fit`, so the order here is the reading
   * order on a wide window and the stacking order on a narrow one — and the
   * documents somebody agrees to belong at the end of the site map rather than
   * in the middle of it, which is where the reader who is looking for them
   * looks first.
   *
   * `LEGAL_NAV` untouched and unwrapped: these are `NavItem`s already, and
   * `ChromeLink` draws them exactly like every other entry in this footer. The
   * one canonical copy stays the one canonical copy; see its note in
   * `@goodbuild/rules/site`.
   */
  const columns: readonly FooterColumn[] = [
    ...chrome.columns,
    { title: 'Legal', items: LEGAL_NAV },
  ];

  return (
    <footer className={styles.footer}>
      <div className={styles.footerInner}>
        <nav className={styles.footerColumns} aria-label="Footer">
          {columns.map((column) => (
            <div key={column.title} className={styles.footerColumn}>
              {/* The 70% ramp value, which is what the guidelines set every
                  meaningful label in. The 40% is document furniture there — a
                  page number and a specimen tag — and at 11px tracked caps it
                  is 3.2:1 on Paper. */}
              <p className="gb-data-label" style={{ color: 'var(--gb-text-muted)', margin: 0 }}>
                {column.title}
              </p>
              <ul>
                {column.items.map((item) => (
                  <li key={item.href}>
                    <ChromeLink item={item} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className={styles.footerBase}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gb-space-3)' }}>
            {/* The stacked lockup: strip above wordmark, the default where
                there is vertical room. */}
            <Logo size="1.25rem" site={site} />
            <p className="gb-ui-label" style={{ margin: 0 }}>
              {chrome.footerLine}
            </p>
            {support ? (
              <p className="gb-ui-label" style={{ margin: 0 }}>
                Support: <a href={`mailto:${support}`}>{support}</a>
              </p>
            ) : null}
            {/* A storefront selling to EU consumers has to identify its
                trader. This is an obligation, not a flourish, and it is the
                same operator on all three sites.

                The country is named rather than left to the full operator
                sentence, which would repeat the company name twice in one
                line. The sentence form lives on /contact and the legal
                pages, where it is the thing being said rather than a
                colophon. */}
            <p className="gb-ui-label" style={{ margin: 0, color: 'var(--gb-text-muted)' }}>
              © {year} {COMPANY.legalName} · Registered in {COMPANY.country}
            </p>
          </div>

          {/*
            The colophon's other half is the social row and nothing else now.

            `.footerLegal` used to sit beside it, holding the same three links
            the `Legal` column above holds. Left here it would be the documents
            said twice in one footer — and a reader who sees `Terms` in two
            places has to decide which of them is the real one, which is the
            exact objection that took a second `/about` link out of the store's
            first column.

            The wrapper stays even with one child in it: it is the right-hand
            end of a `space-between`, and the socials are configuration — an
            unconfigured account renders nothing at all here (section 29.2), and
            the colophon should look the same either way.
          */}
          <div style={{ display: 'flex', gap: 'var(--gb-space-8)', flexWrap: 'wrap' }}>
            {socials.length > 0 && (
              <div className={styles.footerSocials}>
                {socials.map((social) => (
                  <a
                    key={social.label}
                    href={social.url}
                    rel="me noopener noreferrer"
                    target="_blank"
                  >
                    {social.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
